import { Switch } from '@reflex/ui/components/ui/switch';
import { Badge } from '@reflex/ui/components/ui/badge';
import { SectionLabel, PageDescription } from '@reflex/ui/components/layout/Typography';
import { DescriptionHint } from '@reflex/ui/components/shared/DescriptionHint';
import { ProviderChip } from '@reflex/ui/components/agent/ProviderChip';
import type { AgentModelProvider, DiscoveredModel } from '@reflex/shared';

const PROVIDERS_SECTION_DESCRIPTION =
  'Disable a provider to hide all its models from the Launch dialog.';

/**
 * Human-readable auth capabilities for a provider, derived from its declared
 * `keyTypes`. `undefined` defaults to API-key support; an empty array marks a
 * free-tier provider that needs no key.
 */
function providerKeyCapabilities(provider: AgentModelProvider): string[] {
  const keyTypes = provider.keyTypes ?? ['apiKey'];
  if (keyTypes.length === 0) return ['Free tier'];
  const labels: string[] = [];
  if (keyTypes.includes('apiKey')) labels.push('API key');
  if (keyTypes.includes('subscription')) labels.push('Subscription');
  return labels;
}

export interface ProvidersSectionProps {
  providers: AgentModelProvider[];
  modelsByProvider: Record<string, DiscoveredModel[]>;
  isProviderEnabled: (providerId: string) => boolean;
  isPending: boolean;
  onToggleProvider: (providerId: string, enabled: boolean) => void;
  readOnly?: boolean;
}

export function ProvidersSection({
  providers,
  modelsByProvider,
  isProviderEnabled,
  isPending,
  onToggleProvider,
  readOnly = false,
}: ProvidersSectionProps) {
  return (
    <div className="border-t pt-4">
      <div className="mb-3 flex items-center gap-1">
        <SectionLabel className="mb-0">Configure Providers</SectionLabel>
        <DescriptionHint className="sm:hidden" ariaLabel="About this section">
          {PROVIDERS_SECTION_DESCRIPTION}
        </DescriptionHint>
      </div>
      <PageDescription className="mb-3 max-sm:hidden text-xs">
        {PROVIDERS_SECTION_DESCRIPTION}
      </PageDescription>
      <div className="space-y-2">
        {providers.map((provider) => {
          const enabled = isProviderEnabled(provider.id);
          const modelCount = modelsByProvider[provider.id]?.length ?? 0;
          return (
            <div
              key={provider.id}
              className="flex items-center justify-between gap-3 py-1.5 px-2 rounded hover:bg-muted/30"
            >
              <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                <ProviderChip provider={provider.id} name={provider.displayName} />
                <Badge variant="outline" className="text-2xs">
                  {modelCount} model{modelCount !== 1 ? 's' : ''}
                </Badge>
                {providerKeyCapabilities(provider).map((label) => (
                  <Badge key={label} variant="secondary" className="text-2xs">
                    {label}
                  </Badge>
                ))}
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={(checked) => onToggleProvider(provider.id, checked)}
                disabled={readOnly || isPending}
                aria-label={`Toggle ${provider.displayName}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
